/**
 * Zenprexi Bio-Risk™ Supervisor Dashboard
 * 
 * Main application component with routing.
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { store, useAppSelector, useAppDispatch } from '@/store';
import { fetchCurrentUser } from '@/store/authSlice';
import { UserRole } from '@/types';

// Pages
import LoginPage from '@/pages/LoginPage';
import SupervisorPage from '@/pages/SupervisorPage';
import BaselineAdminPage from '@/pages/BaselineAdminPage';

// Layout
import { zbrColor } from '@/theme/zbr';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
    },
  },
});

const APP_BUILD_MARKER = 'zbr-v4-dashboard-cache-20260629';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// App routes
function AppRoutes() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const normalizedRole = (user?.role || '').toString().toUpperCase();
  const defaultAuthenticatedRoute = normalizedRole === UserRole.WORKER ? '/workers' : '/';
  
  // Fetch current user on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(fetchCurrentUser());
    }
  }, [isAuthenticated, user, dispatch]);
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to={defaultAuthenticatedRoute} replace /> : <LoginPage />
      } />
      
      {/* Protected routes */}
      <Route path="/baseline-admin" element={<ProtectedRoute><BaselineAdminPage /></ProtectedRoute>} />
      <Route path="/" element={
        <ProtectedRoute>
          <SupervisorPage />
        </ProtectedRoute>
      } />
      {['/workers', '/workers/:workerId', '/interventions'].map(path => <Route key={path} path={path} element={<ProtectedRoute><SupervisorPage /></ProtectedRoute>} />)}
      <Route path="/alerts" element={<Navigate to="/interventions" replace />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Main App
export default function App() {
  useEffect(() => {
    document.documentElement.dataset.appBuild = APP_BUILD_MARKER;
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: zbrColor.bgCardRaised,
                color: zbrColor.textBright,
                border: `1px solid ${zbrColor.borderLine}`,
              },
              success: {
                iconTheme: {
                  primary: zbrColor.tealCyan,
                  secondary: zbrColor.bgDeepest,
                },
              },
              error: {
                iconTheme: {
                  primary: zbrColor.redSoft,
                  secondary: zbrColor.bgDeepest,
                },
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}
